import { useState, useEffect } from 'react';
import { Pencil, Check, X, Plus, Trash2 } from 'lucide-react';
import api from '../../services/api';

interface Plan {
  key: string;
  name: string;
  price: number;
  billing: string;
  duration: number;
  features: string[];
  badge?: string;
}

export default function AdminPlansPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<Plan | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await api.get('/admin/plans');
      setPlans(res.data.data);
    } catch {
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (plan: Plan) => {
    setEditingKey(plan.key);
    setDraft({ ...plan, features: [...plan.features] });
    setError(null);
    setSuccess(null);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft(null);
  };

  const saveEdit = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.put(`/admin/plans/${draft.key}`, draft);
      setPlans((prev) => prev.map((p) => (p.key === draft.key ? res.data.data : p)));
      setSuccess(`${draft.name} updated successfully`);
      setEditingKey(null);
      setDraft(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const updateDraftField = (field: keyof Plan, value: any) => {
    setDraft((prev) => prev ? { ...prev, [field]: value } : prev);
  };

  const updateFeature = (index: number, value: string) => {
    if (!draft) return;
    const features = [...draft.features];
    features[index] = value;
    setDraft({ ...draft, features });
  };

  const addFeature = () => {
    if (!draft) return;
    setDraft({ ...draft, features: [...draft.features, ''] });
  };

  const removeFeature = (index: number) => {
    if (!draft) return;
    setDraft({ ...draft, features: draft.features.filter((_, i) => i !== index) });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Subscription Plans</h1>
        <p className="text-gray-500 text-sm mt-1">View and edit plan pricing, features, and billing details</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{success}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isEditing = editingKey === plan.key;
          const data = isEditing && draft ? draft : plan;

          return (
            <div key={plan.key} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              {/* Header */}
              <div className={`px-5 py-4 flex items-center justify-between ${
                plan.key === 'ANNUAL' ? 'bg-purple-50 border-b border-purple-100' :
                plan.key === 'MONTHLY' ? 'bg-blue-50 border-b border-blue-100' :
                'bg-gray-50 border-b border-gray-100'
              }`}>
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <input
                      className="text-base font-semibold bg-white border border-gray-300 rounded px-2 py-1 w-36"
                      value={data.name}
                      onChange={(e) => updateDraftField('name', e.target.value)}
                    />
                  ) : (
                    <span className="text-base font-semibold text-gray-900">{data.name}</span>
                  )}
                  {data.badge && (
                    <span className="text-xs bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full font-medium">
                      {data.badge}
                    </span>
                  )}
                </div>
                <span className="text-xs font-medium text-gray-500 bg-white px-2 py-1 rounded border border-gray-200">
                  {plan.key}
                </span>
              </div>

              {/* Body */}
              <div className="px-5 py-4 space-y-4">
                {/* Price */}
                <div className="flex items-center gap-3">
                  <label className="text-xs text-gray-500 w-20 shrink-0">Price (₹)</label>
                  {isEditing ? (
                    <input
                      type="number"
                      min={0}
                      className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                      value={data.price}
                      onChange={(e) => updateDraftField('price', Number(e.target.value))}
                    />
                  ) : (
                    <span className="text-2xl font-bold text-gray-900">₹{data.price}</span>
                  )}
                </div>

                {/* Billing */}
                <div className="flex items-center gap-3">
                  <label className="text-xs text-gray-500 w-20 shrink-0">Billing</label>
                  {isEditing ? (
                    <input
                      className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                      value={data.billing}
                      onChange={(e) => updateDraftField('billing', e.target.value)}
                    />
                  ) : (
                    <span className="text-sm text-gray-700">{data.billing}</span>
                  )}
                </div>

                {/* Duration */}
                <div className="flex items-center gap-3">
                  <label className="text-xs text-gray-500 w-20 shrink-0">Duration (days)</label>
                  {isEditing ? (
                    <input
                      type="number"
                      min={1}
                      className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                      value={data.duration}
                      onChange={(e) => updateDraftField('duration', Number(e.target.value))}
                    />
                  ) : (
                    <span className="text-sm text-gray-700">{data.duration} days</span>
                  )}
                </div>

                {/* Badge (optional) */}
                {isEditing && (
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-500 w-20 shrink-0">Badge</label>
                    <input
                      className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                      placeholder="e.g. Best Value"
                      value={data.badge || ''}
                      onChange={(e) => updateDraftField('badge', e.target.value)}
                    />
                  </div>
                )}

                {/* Features */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-gray-500">Features</label>
                    {isEditing && (
                      <button
                        onClick={addFeature}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                      >
                        <Plus className="h-3 w-3" /> Add
                      </button>
                    )}
                  </div>
                  <ul className="space-y-1.5">
                    {data.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        {isEditing ? (
                          <>
                            <input
                              className="border border-gray-300 rounded px-2 py-1 text-xs w-full"
                              value={f}
                              onChange={(e) => updateFeature(i, e.target.value)}
                            />
                            <button onClick={() => removeFeature(i)} className="text-red-400 hover:text-red-600 mt-1 shrink-0">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-gray-600 flex items-start gap-1.5">
                            <span className="text-green-500 mt-0.5">✓</span> {f}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Actions */}
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
                {isEditing ? (
                  <>
                    <button
                      onClick={cancelEdit}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100"
                    >
                      <X className="h-4 w-4" /> Cancel
                    </button>
                    <button
                      onClick={saveEdit}
                      disabled={saving}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      <Check className="h-4 w-4" /> {saving ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => startEdit(plan)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    <Pencil className="h-4 w-4" /> Edit
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-xs text-gray-400">
        Note: Plan changes apply immediately but reset on server restart. For permanent changes, update the server config.
      </p>
    </div>
  );
}
